// src/components/ConnectorsPage.js

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import "./ConnectorsPage.css";

const PROVIDER_DISPLAY_NAMES = {
  aws: "AWS",
  cloudflare: "Cloudflare",
  github: "GitHub",
  google_workspace: "Google Workspace",
  okta: "Okta",
};

const STREAM_DISPLAY_NAMES = {
  aws_cloudtrail_events: "AWS CloudTrail Events",
  aws_config_resources: "AWS Config Resources",
  aws_iam_inventory: "AWS IAM Inventory",
  aws_s3_posture: "AWS S3 Posture",
  github_repos: "GitHub Repositories",
  google_workspace_activity: "Google Workspace Activity",
  google_workspace_admin: "Google Workspace Admin",
  google_workspace_default: "Google Workspace Default",
  google_workspace_login: "Google Workspace Login",
  okta_default: "Okta Default",
  okta_system_log: "Okta System Log",
  iam: "Legacy AWS IAM State",
};

function cleanProvider(value) {
  return String(value || "").trim().toLowerCase();
}

function formatProvider(value) {
  const normalizedProvider = cleanProvider(value);

  if (PROVIDER_DISPLAY_NAMES[normalizedProvider]) {
    return PROVIDER_DISPLAY_NAMES[normalizedProvider];
  }

  return String(value || "unknown")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function formatStreamName(value) {
  const normalizedStream = cleanProvider(value);

  if (!normalizedStream) return "—";

  if (STREAM_DISPLAY_NAMES[normalizedStream]) {
    return STREAM_DISPLAY_NAMES[normalizedStream];
  }

  return String(value || "unknown")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function safeNumber(value) {
  const next = Number(value);
  return Number.isFinite(next) ? next : 0;
}

function getConnectorId(connector) {
  return connector?.id || connector?.connector_id || "";
}

function formatTimestamp(value) {
  if (!value) return "—";

  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function truncate(value, maxLength = 180) {
  const text = String(value || "").trim();
  if (!text) return "—";
  return text.length > maxLength ? `${text.slice(0, maxLength - 3)}...` : text;
}

function toneForStatus(status) {
  const value = String(status || "").toLowerCase();

  if (["connected", "healthy", "success", "recovered"].includes(value)) {
    return "success";
  }

  if (["degraded", "stale", "running", "queued", "requeued"].includes(value)) {
    return "warning";
  }

  if (["failed", "failing", "unresolved", "disconnected"].includes(value)) {
    return "danger";
  }

  if (["dismissed", "quarantined"].includes(value)) {
    return "info";
  }

  return "neutral";
}

function StatusBadge({ children, tone = "neutral" }) {
  return (
    <span className={`connector-badge connector-badge-${tone}`}>
      {children}
    </span>
  );
}

function StatTile({ label, value, tone = "neutral" }) {
  return (
    <div className={`connector-stat connector-stat-${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function EmptyState({ children }) {
  return <div className="connector-empty-state">{children}</div>;
}

function mergeConnectorWithHealth(connector, healthRows) {
  const connectorId = getConnectorId(connector);
  const provider = cleanProvider(connector?.provider);

  return (
    healthRows.find((row) => row.connector_id === connectorId) ||
    healthRows.find((row) => cleanProvider(row.provider) === provider) ||
    null
  );
}

function HistorySummary({ summary }) {
  const dlq = summary?.dlq?.total || {};

  return (
    <div className="connector-history-summary">
      <StatTile
        label="Active DLQ"
        value={safeNumber(dlq.active)}
        tone={safeNumber(dlq.active) ? "danger" : "success"}
      />
      <StatTile label="Recovered" value={safeNumber(dlq.recovered)} tone="success" />
      <StatTile label="Dismissed" value={safeNumber(dlq.dismissed)} tone="info" />
      <StatTile label="Historical" value={safeNumber(dlq.historical)} tone="neutral" />
      <StatTile
        label="Quarantined"
        value={safeNumber(summary?.quarantined_state_records?.total)}
        tone="info"
      />
      <StatTile
        label="Recovery Jobs"
        value={safeNumber(summary?.recovery_validation_jobs?.total)}
        tone="neutral"
      />
    </div>
  );
}

function DlqHistory({ rows }) {
  if (!rows.length) {
    return <EmptyState>No DLQ history for this provider.</EmptyState>;
  }

  return (
    <div className="connector-history-table-wrap">
      <table className="connector-history-table">
        <thead>
          <tr>
            <th>DLQ</th>
            <th>Stream</th>
            <th>Status</th>
            <th>Error</th>
            <th>Resolved</th>
            <th>Reason</th>
          </tr>
        </thead>

        <tbody>
          {rows.map((item) => (
            <tr key={item.dlq_id}>
              <td className="connector-mono">{item.dlq_id}</td>
              <td className="connector-stream-cell">
                <span className="connector-stream-label">{formatStreamName(item.stream)}</span>
                {item.stream && (
                  <span className="connector-stream-raw connector-mono">{item.stream}</span>
                )}
              </td>
              <td>
                <StatusBadge tone={toneForStatus(item.recovery_status)}>
                  {item.recovery_status || "unknown"}
                </StatusBadge>
              </td>
              <td>
                <strong>{item.error_class || "—"}</strong>
                <span>{truncate(item.error_summary, 110)}</span>
              </td>
              <td>{formatTimestamp(item.resolved_at || item.recovered_at || item.requeued_at)}</td>
              <td>{truncate(item.resolution_reason, 200)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RecoveryJobs({ rows }) {
  if (!rows.length) {
    return <EmptyState>No recovery validation jobs for this provider.</EmptyState>;
  }

  return (
    <div className="connector-history-list">
      {rows.map((job) => {
        const ctx = job.dlq_recovery || {};

        return (
          <article key={job.job_id} className="connector-history-card">
            <div className="connector-history-card-header">
              <div>
                <h4>{formatStreamName(job.stream)}</h4>
                <p className="connector-mono">{job.job_id}</p>
              </div>

              <StatusBadge tone={toneForStatus(job.status)}>
                {job.status || "unknown"}
              </StatusBadge>
            </div>

            <div className="connector-history-meta">
              <span>
                Failure category: <strong>{ctx.failure_category || "—"}</strong>
              </span>
              <span>
                Finished: <strong>{formatTimestamp(job.finished_at)}</strong>
              </span>
              <span>
                Candidates:{" "}
                <strong>{safeArray(ctx.candidate_dlq_ids).join(", ") || "—"}</strong>
              </span>
            </div>

            {ctx.reason && <p>{ctx.reason}</p>}
          </article>
        );
      })}
    </div>
  );
}

function QuarantineHistory({ rows }) {
  if (!rows.length) {
    return <EmptyState>No quarantined sync-state records for this provider.</EmptyState>;
  }

  return (
    <div className="connector-history-list">
      {rows.map((item) => (
        <article key={item.quarantine_id} className="connector-history-card">
          <div className="connector-history-card-header">
            <div>
              <h4>{formatStreamName(item.stream)}</h4>
              <p className="connector-mono">{item.source_sync_state_id}</p>
            </div>

            <StatusBadge tone="info">quarantined</StatusBadge>
          </div>

          <p>{item.quarantine_reason || "No quarantine reason captured."}</p>

          <div className="connector-history-meta">
            <span>
              Quarantined: <strong>{formatTimestamp(item.quarantined_at)}</strong>
            </span>
            <span>
              By: <strong>{item.quarantined_by_name || "—"}</strong>
            </span>
          </div>
        </article>
      ))}
    </div>
  );
}

function StaleJobs({ rows }) {
  if (!rows.length) {
    return <EmptyState>No stale-job reconciliations for this provider.</EmptyState>;
  }

  return (
    <div className="connector-history-list">
      {rows.map((job) => (
        <article key={job.job_id} className="connector-history-card">
          <div className="connector-history-card-header">
            <div>
              <h4>{job.stream ? formatStreamName(job.stream) : "Unscoped sync job"}</h4>
              <p className="connector-mono">{job.job_id}</p>
            </div>

            <StatusBadge tone={toneForStatus(job.status)}>
              {job.status || "unknown"}
            </StatusBadge>
          </div>

          <div className="connector-history-meta">
            <span>
              Started: <strong>{formatTimestamp(job.started_at)}</strong>
            </span>
            <span>
              Finished: <strong>{formatTimestamp(job.finished_at)}</strong>
            </span>
            <span>
              Error: <strong>{job.error_class || "—"}</strong>
            </span>
          </div>
        </article>
      ))}
    </div>
  );
}

function ConnectorHistoryModal({ state, onClose }) {
  const [tab, setTab] = useState("summary");
  const payload = state?.payload || {};
  const provider = state?.provider || payload?.provider_filter || "";

  useEffect(() => {
    setTab("summary");
  }, [provider]);

  if (!state?.open) return null;

  const tabs = [
    ["summary", "Summary"],
    ["dlq", "DLQ History"],
    ["recovery", "Recovery Jobs"],
    ["quarantine", "Quarantine"],
    ["stale", "Stale Jobs"],
  ];

  return (
    <div className="connector-modal-backdrop">
      <div className="connector-modal">
        <header className="connector-modal-header">
          <div>
            <h3>{formatProvider(provider)} Operational History</h3>
            <p>Recovery, dismissal, quarantine, and stale-job audit trail.</p>
          </div>

          <button type="button" onClick={onClose}>
            Close
          </button>
        </header>

        <nav className="connector-modal-tabs">
          {tabs.map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={tab === key ? "active" : ""}
              onClick={() => setTab(key)}
            >
              {label}
            </button>
          ))}
        </nav>

        <section className="connector-modal-body">
          {state.loading ? (
            <EmptyState>Loading operational history…</EmptyState>
          ) : state.error ? (
            <div className="connector-error">{state.error}</div>
          ) : (
            <>
              {tab === "summary" && <HistorySummary summary={payload.summary} />}
              {tab === "dlq" && <DlqHistory rows={safeArray(payload.dlq_history)} />}
              {tab === "recovery" && (
                <RecoveryJobs rows={safeArray(payload.recovery_validation_history)} />
              )}
              {tab === "quarantine" && (
                <QuarantineHistory rows={safeArray(payload.quarantine_history)} />
              )}
              {tab === "stale" && (
                <StaleJobs rows={safeArray(payload.stale_reconciliation_history)} />
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}

export default function ConnectorsPage({ apiBaseUrl, tenantId, userId }) {
  const { getAccessTokenSilently } = useAuth0();

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [health, setHealth] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [note, setNote] = useState("");
  const [historyModal, setHistoryModal] = useState({
    open: false,
    provider: "",
    loading: false,
    error: "",
    payload: null,
  });

  const healthRows = useMemo(() => safeArray(health?.connectors), [health]);

  const apiRequest = useCallback(
    async (path, options = {}) => {
      if (!tenantId || !userId) {
        throw new Error("Connector APIs require valid tenant and user identifiers.");
      }

      const token = await getAccessTokenSilently();

      const response = await fetch(`${apiBaseUrl}${path}`, {
        method: options.method || "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenantId,
          "X-User-Id": userId,
          ...(options.headers || {}),
        },
        body: options.body ? JSON.stringify(options.body) : undefined,
      });

      const text = await response.text();
      let payload = null;

      if (text) {
        try {
          payload = JSON.parse(text);
        } catch {
          payload = text;
        }
      }

      if (!response.ok) {
        const detail =
          payload?.detail?.message ||
          payload?.detail ||
          payload?.message ||
          text ||
          `Request failed with ${response.status}`;

        throw new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
      }

      return payload;
    },
    [apiBaseUrl, getAccessTokenSilently, tenantId, userId]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setNote("");

    try {
      const [rows, healthPayload] = await Promise.all([
        apiRequest("/connectors"),
        apiRequest("/connectors/operational-health"),
      ]);

      setItems(Array.isArray(rows) ? rows : []);
      setHealth(healthPayload || null);
    } catch (err) {
      setNote(`Load failed: ${err.message || String(err)}`);
    } finally {
      setLoading(false);
    }
  }, [apiRequest]);

  useEffect(() => {
    load();
  }, [load]);

  const openHistory = async (provider) => {
    const normalizedProvider = cleanProvider(provider);
    if (!normalizedProvider) return;

    setHistoryModal({
      open: true,
      provider: normalizedProvider,
      loading: true,
      error: "",
      payload: null,
    });

    try {
      const payload = await apiRequest(
        `/connectors/operational-health/history?provider=${encodeURIComponent(
          normalizedProvider
        )}&limit=50`
      );

      setHistoryModal({
        open: true,
        provider: normalizedProvider,
        loading: false,
        error: "",
        payload,
      });
    } catch (err) {
      setHistoryModal({
        open: true,
        provider: normalizedProvider,
        loading: false,
        error: err.message || String(err),
        payload: null,
      });
    }
  };

  const closeHistory = () => {
    setHistoryModal({
      open: false,
      provider: "",
      loading: false,
      error: "",
      payload: null,
    });
  };

  const doValidate = async (id) => {
    setBusyId(id);
    setNote("");

    try {
      const result = await apiRequest(`/connectors/${id}/validate`, {
        method: "POST",
      });

      setNote(
        result?.result === "connected"
          ? "Validation succeeded."
          : result?.error || "Validation completed."
      );

      await load();
    } catch (err) {
      setNote(`Validate failed: ${err.message || String(err)}`);
    } finally {
      setBusyId(null);
    }
  };

  const doRunSync = async (connector) => {
    const id = getConnectorId(connector);
    const provider = cleanProvider(connector?.provider);

    setBusyId(id);
    setNote("");

    try {
      const result = await apiRequest(
        `/sync/run?provider=${encodeURIComponent(provider)}&connector_id=${encodeURIComponent(id)}`,
        { method: "POST" }
      );

      setNote(`Sync queued: job_id ${result?.job_id || "unknown"}`);
      await load();
    } catch (err) {
      setNote(`Run sync failed: ${err.message || String(err)}`);
    } finally {
      setBusyId(null);
    }
  };

  const doRotateExternalId = async (id) => {
    setBusyId(id);
    setNote("");

    try {
      const result = await apiRequest(`/connectors/${id}/rotate-external-id`, {
        method: "POST",
      });

      await navigator.clipboard?.writeText(JSON.stringify(result, null, 2)).catch(() => {});
      setNote("Rotated external id. Trust policy JSON copied to clipboard. Update AWS, then validate.");
      await load();
    } catch (err) {
      setNote(`Rotate failed: ${err.message || String(err)}`);
    } finally {
      setBusyId(null);
    }
  };

  const summary = health?.summary;

  return (
    <div className="connectors-shell">
      <ConnectorHistoryModal state={historyModal} onClose={closeHistory} />

      <div className="connectors-toolbar">
        <div>
          <h3>Connector Operations</h3>
          <p>Monitor integration health, collection status, and recovery lifecycle evidence.</p>
        </div>

        <button type="button" onClick={load} className="connector-primary-action">
          Refresh
        </button>
      </div>

      {summary && (
        <div className="connectors-summary-grid">
          <StatTile label="Connectors" value={summary.total_connectors || 0} tone="neutral" />
          <StatTile label="Healthy" value={summary.by_operational_health?.healthy || 0} tone="success" />
          <StatTile label="Degraded" value={summary.by_operational_health?.degraded || 0} tone="warning" />
          <StatTile label="Failing" value={summary.by_operational_health?.failing || 0} tone="danger" />
          <StatTile label="Stale" value={summary.by_operational_health?.stale || 0} tone="warning" />
        </div>
      )}

      {note && (
        <div className={note.startsWith("Load failed") ? "connector-error" : "connector-note"}>
          {note}
        </div>
      )}

      {loading ? (
        <EmptyState>Loading connectors…</EmptyState>
      ) : items.length === 0 ? (
        <EmptyState>No connectors found.</EmptyState>
      ) : (
        <div className="connectors-grid">
          {items.map((connector) => {
            const connectorId = getConnectorId(connector);
            const healthRecord = mergeConnectorWithHealth(connector, healthRows);
            const provider = cleanProvider(connector.provider || healthRecord?.provider);
            const activeDlq = safeNumber(healthRecord?.dlq_count);

            return (
              <article key={connectorId} className="connector-card">
                <header className="connector-card-header">
                  <div>
                    <h4>{formatProvider(connector.provider)}</h4>
                    <p className="connector-mono">{connectorId}</p>
                  </div>

                  <div className="connector-card-badges">
                    <StatusBadge tone={toneForStatus(connector.status)}>
                      {connector.status || "unknown"}
                    </StatusBadge>

                    {healthRecord?.operational_health && (
                      <StatusBadge tone={toneForStatus(healthRecord.operational_health)}>
                        {healthRecord.operational_health}
                      </StatusBadge>
                    )}
                  </div>
                </header>

                {healthRecord && (
                  <div className="connector-card-stats">
                    <StatTile
                      label="Active DLQ"
                      value={activeDlq}
                      tone={activeDlq ? "danger" : "success"}
                    />
                    <StatTile
                      label="Recovered"
                      value={safeNumber(healthRecord.recovered_dlq_count)}
                      tone="success"
                    />
                    <StatTile
                      label="Dismissed"
                      value={safeNumber(healthRecord.dismissed_dlq_count)}
                      tone="info"
                    />
                    <StatTile
                      label="Historical"
                      value={safeNumber(healthRecord.historical_dlq_count)}
                      tone="neutral"
                    />
                  </div>
                )}

                {healthRecord?.recommended_action && (
                  <p className="connector-recommended-action">
                    {healthRecord.recommended_action}
                  </p>
                )}

                <footer className="connector-card-actions">
                  <button
                    type="button"
                    onClick={() => openHistory(provider)}
                    disabled={!provider || busyId === connectorId}
                  >
                    View history
                  </button>

                  <button
                    type="button"
                    onClick={() => doValidate(connectorId)}
                    disabled={busyId === connectorId}
                  >
                    Validate
                  </button>

                  <button
                    type="button"
                    onClick={() => doRunSync(connector)}
                    disabled={busyId === connectorId}
                  >
                    Run sync
                  </button>

                  {provider === "aws" && (
                    <button
                      type="button"
                      onClick={() => doRotateExternalId(connectorId)}
                      disabled={busyId === connectorId}
                    >
                      Rotate external ID
                    </button>
                  )}
                </footer>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
